import React, { useMemo } from 'react';
import { useT } from '@transifex/react';
import { useIntl } from 'react-intl';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getDistributionChannel, getDistributor, useFeatureFlag } from '@raisin/cp-utils';
import { theme, Typography, Tooltip } from '@raisin/components-lib';
import {
  formatCurrencyPosition,
  getBankChips,
  getDepositGuaranteePayoutCurrency,
  getBankPageLink,
} from 'utils/bankPageUtils';
import { DepositTakingBankListItem } from 'types';
import CountryFlag from '../../CountryFlag';
import messages from '../../messages';
import BankPromotedProduct from '../../BankPromotedProduct/v1';
import { handleBankCardClick } from 'eventsTracking/BankList/onBankCardClicked';
import { BANK_EVENTS } from 'eventsTracking';
import BankProductChip from '../../BankProductChips';
import * as S from './styles';

export interface BankListItemProps {
  bank: DepositTakingBankListItem;
  querySelector: string;
}

const BankListItem: React.FC<BankListItemProps> = ({ bank, querySelector }) => {
  const t = useT();
  const { variation } = useFeatureFlag();
  const { formatNumber, locale } = useIntl();
  const distributionChannel = getDistributionChannel(locale);
  const shouldUseBankSlugFallback = variation?.('b2CShouldUseBankSlugFallback', false);
  const distributorId = getDistributor();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const promotedProduct = bank?.deposit_products?.promoted_products?.[0];
  const currency = bank?.conditions?.currency || undefined;

  const bankDepositGuaranteeCurrency = useMemo(
    () =>
      getDepositGuaranteePayoutCurrency(
        bank?.statutory_deposit_guarantee_scheme?.country?.id,
        distributionChannel,
      ) || t(messages.bankListItemNoCurrency),
    [bank?.statutory_deposit_guarantee_scheme?.country?.id, distributionChannel],
  );

  const hasActiveProducts = bank?.deposit_products !== null;

  const minInvestmentAmount = formatCurrencyPosition(
    formatNumber(
      bank.conditions.investment_amount_min ? +bank.conditions.investment_amount_min : 0,
      {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0,
      },
    ),
    locale,
  );
  const maxInvestmentAmount = formatCurrencyPosition(
    formatNumber(+bank.conditions.investment_amount_max, {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0,
    }),
    locale,
  );

  const depositGuarantee = formatCurrencyPosition(
    formatNumber(+bank.statutory_deposit_guarantee_scheme.insured_amount, {
      style: 'currency',
      currency: bank.statutory_deposit_guarantee_scheme.currency,
      maximumFractionDigits: 0,
    }),
    locale,
  );

  const bankChipsLength = useMemo(
    () => getBankChips(bank, distributionChannel).length,
    [bank, distributionChannel],
  );

  return (
    <S.CustomLink
      role="button"
      href={getBankPageLink(locale, shouldUseBankSlugFallback, bank.bank_slug, bank.name)}
      data-testid="bank-list-item"
      className={querySelector}
    >
      <S.BankCard
        data-testid="bank-card"
        onClick={(e) => {
          e.stopPropagation();
          handleBankCardClick({
            bankId: bank.id,
            promotedProductId: promotedProduct?.id,
            distributorId,
            eventName: BANK_EVENTS.BANK_CARD_CLICKED,
          });
        }}
      >
        <S.BankDetails $fullWidth={!promotedProduct}>
          <S.BankTitle variant="h3">{bank.name}</S.BankTitle>
          <S.DetailsContainer>
            <S.DetailItem>
              <Typography sx={{ mt: 2, mb: 5 }} variant="body1">
                {bank.statutory_deposit_guarantee_scheme.country.name}
                <CountryFlag
                  countryId={bank.statutory_deposit_guarantee_scheme.country.id}
                  altText={bank.statutory_deposit_guarantee_scheme.country.name}
                />
                <S.TooltipContent>
                  {bank.statutory_deposit_guarantee_scheme.country.rating && (
                    <Tooltip content={t(messages.banksRatingTooltip)}>
                      <span>({bank.statutory_deposit_guarantee_scheme.country.rating})</span>
                    </Tooltip>
                  )}
                </S.TooltipContent>
              </Typography>

              <Typography mb={2} variant="body1">
                {hasActiveProducts ? (
                  <>
                    <strong>{t(messages.banksDepositAmountTitle)}</strong> {isMobile && <br />}
                    {t(messages.banksDepositAmountValue, {
                      minAmount: minInvestmentAmount,
                      maxAmount: maxInvestmentAmount,
                    })}
                  </>
                ) : (
                  <strong>{t(messages.bankListItemNoActiveProducts)}</strong>
                )}
              </Typography>
              <Typography variant="body1">
                <strong>{t(messages.banksProtectionLimitTitle)}</strong> {isMobile && <br />}
                {t(messages.banksProtectionLimitValue, {
                  amount: depositGuarantee,
                  currency: bankDepositGuaranteeCurrency,
                })}
              </Typography>
              {bankChipsLength > 0 && (
                <S.BenefitsContainer display="flex" alignItems="center" gap={1}>
                  <BankProductChip bank={bank} distributionChannel={distributionChannel} />
                </S.BenefitsContainer>
              )}
            </S.DetailItem>
            {promotedProduct && (
              <S.PromotedProduct>
                {<BankPromotedProduct product={promotedProduct} />}
              </S.PromotedProduct>
            )}
          </S.DetailsContainer>
        </S.BankDetails>
      </S.BankCard>
    </S.CustomLink>
  );
};

export default BankListItem;
